<template>
  <div
    v-if="section!==null"
    v-wechat-title="$route.meta.title"
    class="app-container"
    @scroll="handleScroll"
  >
    <!--导航栏-->
    <van-nav-bar
      fixed
      :z-index="1000"
    >
      <span slot="left">{{ section.name }}</span>
      <template slot="right">
        <!--关键字标签-->
        <van-tag v-if="keyword" class="keyword-tag" color="#ff4444">
          {{ keyword }}
          <!--清空图标-->
          <van-icon
            name="error"
            color="#fff"
            size="14px"
            class-prefix="iconfont"
            @click="clearKeyword"
          />
        </van-tag>
        <van-icon
          name="search"
          color="#333"
          size="20px"
          class-prefix="iconfont"
          @click="openSearchView"
        />
      </template>
    </van-nav-bar>
    <div class="article-list-wrapper">
      <!--文章标签页-->
      <van-tabs
        v-model="currentTabIndex"
        sticky
        :offset-top="46"
        swipeable
        animated
        :swipe-threshold="5"
        :class="{'hide-nav':articleListProps.length==1}"
        @click="handleTabClick"
        @change="handleTabChange"
      >
        <van-tab
          v-for="(props,index) in articleListProps"
          :key="props.sectionCode"
          :title="props.sectionName"
        >
          <!--文章列表-->
          <article-list
            :ref="'articleList'+index"
            :page-size="props.pageSize"
            :section-code="props.sectionCode"
            :keyword="props.keyword"
            :min-height="articleListHeight"
            @load="handleArticleListLoad($event,index)"
            @refresh="handleArticleListRefresh($event,index)"
          />
        </van-tab>
      </van-tabs>
    </div>
    <!--侧边栏-->
    <div v-show="scrollTop>200" class="block-sidebar">
      <!--置顶-->
      <van-icon
        name="top"
        class="block-sidebar-item top"
        color="#fff"
        size="24px"
        class-prefix="iconfont"
        @click="scrollToTop(true)"
      />
      <!--分页-->
      <div class="block-sidebar-item pagination">
        <div class="page-number">
          {{ pageNumber }}
        </div>
        <div class="page-count">
          {{ pageCount }}
        </div>
      </div>
    </div>
    <!--搜索弹出层-->
    <van-popup
      v-model="searchViewOpened"
      :overlay="false"
      position="top"
      transition="van-slide-up"
      style="height:100vh"
    >
      <!--搜索视图-->
      <search-view
        ref="searchView"
        v-model="keyword"
        @search="handleArticleListSearch"
        @back="closeSearchView"
      />
    </van-popup>
  </div>
</template>

<script>
import { getSection } from '@/api/article'
import { ArticleList } from './components'
import SearchView from '@/components/SearchView'
import { scrollToTop } from '@/utils'
import { Toast } from 'vant'

export default {
  components: {
    SearchView,
    ArticleList
  },
  data() {
    return {
      section: null,
      articleListProps: [],
      currentTabIndex: 0,
      tabChanging: false,
      searchViewOpened: false,
      searchViewFocused: false
    }
  },
  computed: {
    articleListHeight: function() {
      let articleListHeight = document.documentElement.clientHeight - 46
      if (this.section.children.length > 0) articleListHeight -= 44
      return articleListHeight
    },
    scrollTop: {
      get() {
        return this.articleListProps[this.currentTabIndex].scrollTop
      },
      set(value) {
        this.articleListProps[this.currentTabIndex].scrollTop = value
      }
    },
    pageSize: {
      get() {
        return this.articleListProps[this.currentTabIndex].pageSize
      },
      set(value) {
        this.articleListProps[this.currentTabIndex].pageSize = value
      }
    },
    keyword: {
      get() {
        return this.articleListProps[this.currentTabIndex].keyword
      },
      set(value) {
        this.articleListProps[this.currentTabIndex].keyword = value
      }
    },
    pageNumber: {
      get() {
        return this.articleListProps[this.currentTabIndex].pageNumber
      },
      set(value) {
        this.articleListProps[this.currentTabIndex].pageNumber = value
      }
    },
    pageCount: {
      get() {
        return this.articleListProps[this.currentTabIndex].pageCount
      },
      set(value) {
        this.articleListProps[this.currentTabIndex].pageCount = value
      }
    }
  },
  created() {
    const sectionCode = this.$route.params.sectionCode || this.$route.query.sectionCode
    this.loadData(sectionCode)
  },
  mounted() {
    // 提示正在加载
    Toast.loading({
      message: '加载中...',
      duration: 0
    })
  },
  activated() {
    if (this.articleListProps.length > 0) {
      // 恢复当前标签页垂直滚动位置
      this.$el.scrollTop = this.scrollTop
    }
  },
  beforeRouteLeave(to, from, next) {
    // 监听返回事件，若搜索视图为激活状态，则关闭搜索视图
    if (this.searchViewOpened) {
      this.closeSearchView()
      next(false)
    } else {
      next()
    }
  },
  methods: {
    // 加载数据
    loadData(sectionCode) {
      getSection({
        code: sectionCode,
        eagerLoadingProperties: 'Children'
      }).then(response => {
        // 清除提示
        Toast.clear()
        // 设置栏目数据
        this.section = response.data
        // 设置浏览器标题
        this.$route.meta.title = this.section.name
        // 设置文章列表属性
        this.articleListProps.push({
          sectionName: '全部',
          sectionCode: this.section.code,
          pageSize: 5,
          keyword: '',
          scrollTop: 0,
          pageNumber: 0,
          pageCount: 0
        })
        for (const child of this.section.children) {
          this.articleListProps.push({
            sectionName: child.name,
            sectionCode: child.code,
            pageSize: 5,
            keyword: '',
            scrollTop: 0,
            pageNumber: 0,
            pageCount: 0
          })
        }
      }).catch(error => {
        console.info(error)
        // 设置提示信息
        let message
        if (error.response && error.response.status === 404) {
          message = '未找到指定栏目'
        } else {
          message = error.message
        }
        // 提示异常
        Toast.fail(message)
      })
    },
    // 获取当前文章列表
    getCurrentArticleList() {
      if (this.$refs['articleList' + this.currentTabIndex]) {
        return this.$refs['articleList' + this.currentTabIndex][0]
      } else {
        return undefined
      }
    },
    // 滚动至顶部
    scrollToTop(animated) {
      if (animated) {
        scrollToTop(this.$el, 0, 200)
      } else {
        this.$el.scrollTop = 0
      }
    },
    // 打开搜索视图
    openSearchView() {
      this.searchViewOpened = true
      this.$nextTick(() => {
        this.$refs.searchView.focus()
      })
    },
    // 关闭搜索视图
    closeSearchView() {
      this.searchViewOpened = false
    },
    // 清空关键字
    clearKeyword() {
      this.keyword = ''
      this.$nextTick(() => {
        this.refreshArticleList()
      })
    },
    // 刷新文章列表
    refreshArticleList() {
      this.scrollToTop()
      this.getCurrentArticleList().refresh()
    },
    // 更新当前页码
    updatePageNumber() {
      let currentItemIndex = 0
      const currentArticleList = this.getCurrentArticleList()
      if (currentArticleList) {
        const elements = currentArticleList.$el.querySelectorAll('.van-list>div:not(.van-list__loading)')
        if (elements.length > 0) {
          let offsetTop = 0
          for (; offsetTop < this.scrollTop; currentItemIndex++) {
            offsetTop += elements[currentItemIndex].clientHeight + 10
          }
        }
        if (currentItemIndex % this.pageSize === 0) {
          this.pageNumber = parseInt(currentItemIndex / this.pageSize)
        } else {
          this.pageNumber = parseInt(currentItemIndex / this.pageSize) + 1
        }
      }
    },
    // 处理滚动事件
    handleScroll(event) {
      // 设置垂直滚动位置
      this.scrollTop = event.target.scrollTop
      // 更新页码
      this.updatePageNumber()
    },
    // 处理标签点击事件
    handleTabClick(index, title) {
      if (this.getCurrentArticleList()) {
        if (!this.tabChanging) {
          this.$nextTick(() => {
            if (this.scrollTop === 0) {
              // 若处于顶部，则刷新列表
              this.refreshArticleList()
            } else {
              // 否则，滚动至顶部
              this.scrollToTop(true)
            }
          })
        }
      }
    },
    // 处理标签改变事件
    handleTabChange(index, title) {
      // 设置标签页状态为正在改变
      this.tabChanging = true
      this.$nextTick(() => {
        // 恢复当前标签页垂直滚动位置
        this.$el.scrollTop = this.scrollTop
        // 设置标签页状态为已改变
        this.tabChanging = false
      })
    },
    // 处理文章列表加载事件
    handleArticleListLoad(data, index) {
      if (index !== undefined) {
        this.articleListProps[index].pageCount = data.pageCount
      } else {
        this.pageCount = data.pageCount
      }
    },
    // 处理文章列表刷新事件
    handleArticleListRefresh(data, index) {
      if (index !== undefined) {
        this.articleListProps[index].pageCount = data.pageCount
      } else {
        this.pageCount = data.pageCount
      }
    },
    // 处理文章列表搜索事件
    handleArticleListSearch(keyword) {
      // 关闭搜索视图
      this.closeSearchView()
      // 设置关键字
      this.keyword = keyword
      // 刷新文章列表
      this.$nextTick(() => {
        this.refreshArticleList()
      })
    }
  }
}
</script>

<style lang="scss">
.van-tab {
  color: #333;
  font-size: 15px;
  font-weight: 700;
}
.van-tab.van-tab--active {
  color: #ff4444;
}
.van-tabs.nav-right-fixed {
  .van-tabs__wrap {
    right: 44px;
  }
}
.van-tabs.hide-nav {
  .van-tabs__wrap {
  display: none;
  }
}
</style>

<style scoped lang="scss">
.app-container {
  height: 100vh;
  overflow-y: auto;
}
.app-container::-webkit-scrollbar {
  display: none;
}
.van-nav-bar {
  .van-nav-bar__left {
    color: #333;
    font-size: 16px;
    font-weight: 700;
  }
}
// .van-tabs.nav-right-fixed {
//   .van-tabs__wrap {
//     .nav-right {
//       width: 44px;
//       height: 44px;
//       line-height: 44px;
//       text-align: center;
//       position: fixed;
//       right: 0px;
//       background-color: #fff;
//     }
//   }
// }
.van-tabs--line.hide-nav{
  padding-top: 0px;
}
.keyword-tag {
  margin-right: 10px;
  vertical-align: 3px;
}
.article-list-wrapper {
  margin-top: 46px;
}
.block-sidebar {
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
  .block-sidebar-item {
    display: block;
    width: 40px;
    height: 40px;
    margin-bottom: 10px;
    border-radius: 20px;
    color: #fff;
    background-color: rgba(204, 204, 204, 0.5);
    text-align: center;
  }
  .block-sidebar-item.top {
    line-height: 40px;
  }
  .block-sidebar-item.pagination {
    line-height: 20px;
    font-size: 12px;
    .page-number {
      width: 30px;
      margin: 0 auto;
      font-weight: 700;
    }
    .page-count {
      width: 30px;
      margin: 0 auto;
      border-top: 1px solid #fff;
    }
  }
}
</style>
